import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Card, Form, Input, Upload, Button, Space, message, List, Popconfirm } from 'antd';
import { InboxOutlined, UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './AudienceLab.css';
import api from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';

function AudienceLab() {
  const { Title, Paragraph } = Typography;
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [datasets, setDatasets] = useState([]); // dataset-level list
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  
  const { isAuthenticated } = useAuth();

  const loadDatasets = async () => {
    try {
      const data = await api.get('/audience-datasets?limit=200&offset=0');
      // Backend returns { items, limit, offset, count }
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setDatasets(items);
    } catch (e) {
      message.error(e.message || 'Failed to load datasets');
    }
  };

  const handleDeleteDataset = async (dataset) => {
    try {
      if (!isAuthenticated()) {
        message.error('Please log in to delete datasets');
        navigate('/login');
        return;
      }
      await api.delete(`/audience-datasets/${dataset.id}`);
      message.success('Dataset deleted');
      await loadDatasets();
    } catch (e) {
      if (e?.status === 401) {
        message.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }
      message.error(e?.message || 'Delete failed');
    }
  };

  useEffect(() => {
    loadDatasets();
  }, []);

  const uploadProps = useMemo(() => ({
    multiple: false,
    accept: '.csv,.xls,.xlsx',
    beforeUpload: (file) => {
      setFileList([file]);
      return false; // prevent auto upload
    },
    onRemove: () => {
      setFileList([]);
    },
    fileList,
  }), [fileList]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!fileList.length) {
        message.error('Please select a CSV or Excel file');
        return;
      }
      const file = fileList[0];
      const formData = new FormData();
      formData.append('datasetName', values.datasetName);
      formData.append('file', file);
      setSubmitting(true);
      const res = await fetch('/api/audience-lab/upload', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Upload failed');
      }
      await loadDatasets();
      setFileList([]);
      form.resetFields();
      message.success('Audience dataset uploaded');
    } catch (e) {
      if (e?.errorFields) return; // antd validation
      message.error(e.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="audience-lab-page">
      <Space align="center" className="audience-lab-header">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/superadmin')}>Back to Home</Button>
        <Title level={3} style={{ margin: 0 }}>AudienceLab</Title>
      </Space>

      <Card title="Upload Audience Data" style={{ marginBottom: 16 }}>
        <Paragraph>
          Upload your audience database to analyze against target locations. This will be your searchable profile data.
        </Paragraph>
        <Title level={5} style={{ marginTop: 0 }}>Upload Audience Database</Title>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Upload a CSV or Excel file containing your audience profiles with address data.
        </Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item label="Dataset Name" name="datasetName" rules={[{ required: true, message: 'Please enter a dataset name' }]}>
            <Input placeholder="e.g., Q3 VIP Customers" />
          </Form.Item>
          <Form.Item label="Select File" required>
            <Upload.Dragger {...uploadProps} disabled={submitting}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">CSV, XLS, or XLSX</p>
            </Upload.Dragger>
          </Form.Item>
          {/* Select Columns UI removed as requested */}
          <Space>
            <Button type="primary" icon={<UploadOutlined />} onClick={handleSubmit} loading={submitting} disabled={submitting}>Upload Audience Data</Button>
          </Space>
        </Form>
      </Card>

      {fileList.length > 0 ? (
        <Card title="Selected File">
          <List
            dataSource={fileList}
            renderItem={(f) => (
              <List.Item>
                <List.Item.Meta title={f.name} />
              </List.Item>
            )}
          />
        </Card>
      ) : (
        <Card title="Your Datasets" extra={<Button onClick={loadDatasets}>Refresh</Button>}>
          {datasets.length === 0 ? (
            <Paragraph type="secondary">No datasets uploaded yet. Upload a CSV/XLS/XLSX above, then click Refresh.</Paragraph>
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={datasets}
              renderItem={(d) => (
                <List.Item
                  actions={[
                    <Popconfirm
                      key="confirm-del"
                      title="Delete dataset?"
                      description={`This will remove '${d.name || d.original_filename || `Dataset #${d.id}`}' and all its data.`}
                      okText="Delete"
                      okType="danger"
                      cancelText="Cancel"
                      onConfirm={() => handleDeleteDataset(d)}
                    >
                      <Button danger>Delete</Button>
                    </Popconfirm>
                  ]}
                >
                  <List.Item.Meta
                    title={d.name || d.original_filename || `Dataset #${d.id}`}
                    description={`Rows: ${d.row_count ?? 0} • Created ${d.created_at ? new Date(d.created_at).toLocaleString() : 'N/A'}`}
                  />
                </List.Item>
              )}
            />
          )}
        </Card>
      )}
    </div>
  );
}

export default AudienceLab;
